import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { generateQueryString } from '../utils/functions';

/**
 * useAdvancedFilters - Advanced hook for table filtering with URL synchronization
 *
 * Features:
 * - Automatic URL query parameter sync
 * - Support for multiple filter types
 * - Page management
 * - Compatible with existing generateQueryString utility
 * - Backward compatible with useCommonFilters
 *
 * @param {Object} config - Configuration options
 * @param {Array} config.allowedFilters - Whitelist of allowed filter keys (optional)
 * @param {string} config.pageParam - Page parameter name (default: 'page')
 * @param {boolean} config.resetPageOnFilter - Reset to page 1 on filter change (default: true)
 * @param {boolean} config.useWindowReplace - Use window.location.replace (default: false)
 *
 * @returns {Object} Filter utilities and state
 */
const useAdvancedFilters = (config = {}) => {
  const {
    allowedFilters = [],
    pageParam = 'page',
    resetPageOnFilter = true,
    useWindowReplace = false,
  } = config;

  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});

  // Memoize config values to prevent recreating them on every render
  const allowedFiltersArray = JSON.stringify(allowedFilters);

  // Parse and validate filters from URL
  const parseFiltersFromURL = useCallback(() => {
    const params = queryString.parse(location.search);
    const validFilters = {};
    const allowedFiltersArr = JSON.parse(allowedFiltersArray);

    if (allowedFiltersArr.length > 0) {
      // Use whitelist if provided
      allowedFiltersArr.forEach(key => {
        if (params[key] && String(params[key]).trim()) {
          validFilters[key] = String(params[key]).trim();
        }
      });
    } else {
      // Use all params except page
      Object.keys(params).forEach(key => {
        if (key !== pageParam && params[key] && String(params[key]).trim()) {
          validFilters[key] = String(params[key]).trim();
        }
      });
    }

    return validFilters;
  }, [location.search, allowedFiltersArray, pageParam]);

  // Update filters state when URL changes
  useEffect(() => {
    setFilters(parseFiltersFromURL());
  }, [parseFiltersFromURL]);

  // Get current page from URL
  const getCurrentPage = useCallback(() => {
    const params = queryString.parse(location.search);
    return parseInt(params[pageParam]) || 1;
  }, [location.search, pageParam]);

  // Update URL with new filters
  const updateURL = useCallback(
    (newFilters, options = {}) => {
      const { resetPage = resetPageOnFilter } = options;

      const currentParams = queryString.parse(location.search);
      const updatedParams = { ...currentParams, ...newFilters };

      // Remove empty values
      Object.keys(updatedParams).forEach(key => {
        const value = updatedParams[key];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          delete updatedParams[key];
        }
      });

      // Reset page if needed
      if (resetPage && Object.keys(newFilters).some(key => key !== pageParam)) {
        updatedParams[pageParam] = 1;
      }

      // Build query string using existing utility
      const queryPairs = Object.entries(updatedParams).map(([key, val]) => [key, val]);
      const queryStr = generateQueryString(...queryPairs);

      const newPath = `${location.pathname}${queryStr}`;

      if (useWindowReplace) {
        window.location.replace(newPath);
      } else {
        navigate(newPath, { replace: false });
      }
    },
    [location.pathname, location.search, navigate, pageParam, resetPageOnFilter, useWindowReplace]
  );

  // Apply single filter
  const applyFilter = useCallback(
    (key, value) => {
      updateURL({ [key]: value });
    },
    [updateURL]
  );

  // Apply multiple filters at once
  const applyFilters = useCallback(
    newFilters => {
      updateURL(newFilters);
    },
    [updateURL]
  );

  // Remove single filter
  const removeFilter = useCallback(
    key => {
      updateURL({ [key]: undefined });
    },
    [updateURL]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    const currentPage = getCurrentPage();

    if (useWindowReplace) {
      window.location.replace(`${location.pathname}?${pageParam}=${currentPage}`);
    } else {
      navigate(`${location.pathname}?${pageParam}=${currentPage}`, { replace: false });
    }
  }, [location.pathname, navigate, pageParam, getCurrentPage, useWindowReplace]);

  // Get filter value by key
  const getFilterValue = useCallback(
    key => {
      return filters[key] || '';
    },
    [filters]
  );

  // Check if any filters are active
  const hasActiveFilters = useCallback(() => {
    return Object.keys(filters).length > 0;
  }, [filters]);

  // Set page number
  const setPage = useCallback(
    page => {
      updateURL({ [pageParam]: page }, { resetPage: false });
    },
    [updateURL, pageParam]
  );

  // Build query string for API calls
  const buildQueryString = useCallback(
    (additionalParams = {}) => {
      const params = queryString.parse(location.search);
      const allParams = { ...params, ...additionalParams };

      const queryPairs = Object.entries(allParams)
        .filter(([, val]) => val !== '' && val !== null && val !== undefined)
        .map(([key, val]) => [key, val]);

      return generateQueryString(...queryPairs);
    },
    [location.search]
  ); // Only depend on location.search, not the entire location object

  // Legacy support - handleSearch for backward compatibility
  const handleSearch = useCallback(
    (selectedKeys, dataIndex) => {
      applyFilter(dataIndex, selectedKeys?.[0] || '');
    },
    [applyFilter]
  );

  // Legacy support - handleReset for backward compatibility
  const handleReset = useCallback(
    dataIndex => {
      removeFilter(dataIndex);
    },
    [removeFilter]
  );

  return {
    // Core functionality
    filters,
    applyFilter,
    applyFilters,
    removeFilter,
    clearAllFilters,
    getFilterValue,
    hasActiveFilters,

    // Page management
    currentPage: getCurrentPage(),
    page: getCurrentPage(),
    setPage,

    // Query building
    buildQueryString,

    // Legacy support
    handleSearch,
    handleReset,

    // Raw params
    params: queryString.parse(location.search),
  };
};

export default useAdvancedFilters;
